import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Bell, MapPin, Mail, User, LogOut, RefreshCw, Shield, BarChart3, Users, Building2, AlertTriangle, Music, Merge, ChevronDown, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

function useRelativeTime(date: Date | null): string | null {
  const [, tick] = useState(0);

  useEffect(() => {
    if (!date) return;
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [date]);

  return date ? formatDistanceToNow(date) : null;
}

const mainNavItems = [
  { path: '/todo', label: 'Todo', icon: Bell },
  { path: '/venues', label: 'Venues', icon: MapPin },
  { path: '/concerts', label: 'Concerts', icon: Music },
  { path: '/inbox', label: 'Inbox', icon: Mail },
  { path: '/profile', label: 'Profile', icon: User },
];

const adminNavItems = [
  { path: '/admin/stats', label: 'Statistics', icon: BarChart3 },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/security', label: 'Security', icon: ShieldAlert },
];

const coreVenuesDropdownItems = [
  { path: '/admin/core-venues', label: 'Manage', icon: Building2 },
  { path: '/admin/corrections', label: 'Corrections', icon: AlertTriangle },
  { path: '/admin/disambiguate', label: 'Disambiguate', icon: Merge },
];

interface NavbarProps {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  onSync: () => void;
  unreadEmailCount: number;
  scheduledEmailCount: number;
  overdueTodoCount: number;
  dueTodoCount: number;
}

export function Navbar({ isSyncing, lastSyncAt, onSync, unreadEmailCount, scheduledEmailCount, overdueTodoCount, dueTodoCount }: NavbarProps) {
  const { user, logout } = useAuth();
  const gmailGranted = user?.gmail_permissions_granted ?? false;
  const location = useLocation();
  const navigate = useNavigate();
  const relativeTime = useRelativeTime(lastSyncAt);
  const [coreVenuesOpen, setCoreVenuesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdminMode = location.pathname.startsWith('/admin');
  const navItems = isAdminMode ? adminNavItems : mainNavItems;

  const isCoreVenuesActive = coreVenuesDropdownItems.some((item) =>
    location.pathname.startsWith(item.path)
  );

  const toggleAdminMode = () => {
    if (isAdminMode) {
      navigate('/todo');
    } else {
      navigate('/admin/stats');
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCoreVenuesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-dark-surface border-b border-dark-border shadow-lg backdrop-blur-xs bg-dark-surface/95 sticky top-0 z-40">
      <div className="max-w-(--breakpoint-2xl) mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3 group">
            <div className="nav-accent-logo w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-primary-900/50 transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-primary-900/70">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <span className="hidden sm:block text-2xl font-bold text-dark-text-primary tracking-tight transition-all duration-300 group-hover:text-primary-400">
              BookMyBand
            </span>
          </div>

          {/* Navigation Links */}
          <div className="flex items-center gap-2">
            {navItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) =>
                  clsx(
                    'relative flex items-center gap-2 px-3 py-2 md:px-5 md:py-2.5 rounded-xl font-semibold transition-all duration-300',
                    'hover:scale-105 active:scale-95',
                    isActive
                      ? 'nav-accent-pill text-white'
                      : 'text-dark-text-secondary hover:text-dark-text-primary hover:bg-dark-elevated hover:shadow-md'
                  )
                }
              >
                <Icon className="w-5 h-5" />
                <span className="hidden sm:inline">{label}</span>
                {path === '/inbox' && unreadEmailCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 bg-red-500 text-white text-[0.65rem] font-bold rounded-full flex items-center justify-center leading-none"
                    title={`${unreadEmailCount} unread email${unreadEmailCount === 1 ? '' : 's'}`}
                  >
                    {unreadEmailCount > 99 ? '99+' : unreadEmailCount}
                  </span>
                )}
                {path === '/inbox' && scheduledEmailCount > 0 && (
                  <span
                    className="absolute -bottom-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 bg-yellow-500 text-dark-bg text-[0.65rem] font-bold rounded-full flex items-center justify-center leading-none"
                    title={`${scheduledEmailCount} scheduled email${scheduledEmailCount === 1 ? '' : 's'}`}
                  >
                    {scheduledEmailCount > 99 ? '99+' : scheduledEmailCount}
                  </span>
                )}
                {path === '/todo' && (overdueTodoCount + dueTodoCount) > 0 && (
                  <span className={clsx(
                    'absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-0.5 text-white text-[0.65rem] font-bold rounded-full flex items-center justify-center leading-none',
                    overdueTodoCount > 0 ? 'bg-red-500' : 'bg-amber-500'
                  )}>
                    {(overdueTodoCount + dueTodoCount) > 99 ? '99+' : overdueTodoCount + dueTodoCount}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Core Venues dropdown (admin only) */}
            {isAdminMode && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setCoreVenuesOpen((o) => !o)}
                  className={clsx(
                    'flex items-center gap-2.5 px-5 py-2.5 rounded-xl font-semibold transition-all duration-300',
                    'hover:scale-105 active:scale-95',
                    isCoreVenuesActive
                      ? 'nav-accent-pill text-white'
                      : 'text-dark-text-secondary hover:text-dark-text-primary hover:bg-dark-elevated hover:shadow-md'
                  )}
                >
                  <Building2 className="w-5 h-5" />
                  Core Venues
                  <ChevronDown className={clsx('w-4 h-4 transition-transform', coreVenuesOpen && 'rotate-180')} />
                </button>

                {coreVenuesOpen && (
                  <div className="absolute top-full left-0 mt-1 w-44 bg-dark-surface border border-dark-border rounded-xl shadow-xl overflow-hidden z-50">
                    {coreVenuesDropdownItems.map(({ path, label, icon: Icon }) => (
                      <NavLink
                        key={path}
                        to={path}
                        onClick={() => setCoreVenuesOpen(false)}
                        className={({ isActive }) =>
                          clsx(
                            'flex items-center gap-2.5 px-4 py-3 text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary-900/40 text-primary-400'
                              : 'text-dark-text-secondary hover:text-dark-text-primary hover:bg-dark-elevated'
                          )
                        }
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* User info, admin toggle, sync, and logout */}
            <div className="ml-4 pl-4 border-l border-dark-border flex items-center gap-3">
              <span className="hidden md:block text-sm text-dark-text-secondary">{user?.email}</span>
              {user?.is_admin && (
                <button
                  onClick={toggleAdminMode}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300',
                    isAdminMode
                      ? 'text-primary-400 bg-primary-900/30'
                      : 'text-dark-text-secondary hover:text-primary-400 hover:bg-dark-elevated'
                  )}
                  title={isAdminMode ? 'Back to app' : 'Admin panel'}
                >
                  <Shield className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={gmailGranted ? onSync : undefined}
                disabled={isSyncing || !gmailGranted}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300',
                  gmailGranted
                    ? 'text-dark-text-secondary hover:text-primary-400 hover:bg-dark-elevated'
                    : 'text-dark-text-muted opacity-40 cursor-not-allowed',
                  isSyncing && 'cursor-wait'
                )}
                title={
                  !gmailGranted
                    ? 'You must grant email permissions to sync emails. Go to Profile → Account → Grant Permissions'
                    : isSyncing
                    ? 'Syncing...'
                    : relativeTime
                    ? `Sync emails — last sync ${relativeTime} ago`
                    : 'Sync emails'
                }
              >
                <RefreshCw className={clsx('w-4 h-4', isSyncing && 'animate-spin')} />
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-dark-text-secondary hover:text-red-400 hover:bg-dark-elevated transition-all duration-300"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
